import { ApiError, CancelError } from '@api'
import { AxiosError, isAxiosError, isCancel } from 'axios'
import { ZodError } from 'zod'

import { isCUI } from '@/consts/routes'
import { toast } from '@/models/toasts'
import { isOFLogoutError } from '@/modules/chatting/utils/OFLogout'

const defaultErrorMessage = 'Something went wrong'

export const getErrorMessage = (
  error: unknown,
  fallbackMessage = defaultErrorMessage,
) => {
  let message = fallbackMessage

  if (error instanceof Error && error.message) {
    message = error.message
  }

  if (isAxiosError(error)) {
    if (error.response?.data.detail) {
      message = error.response?.data.detail
    }
    if (error.response?.data && typeof error.response?.data === 'string') {
      message = String(error.response?.data)
    }
  } else if (error instanceof ApiError && error.body.detail) {
    return error.body.detail
  } else if (typeof error === 'string') {
    return error
  }

  return message
}

const getErrorCode = (error: unknown) => {
  return error instanceof ApiError
    ? error.status
    : error instanceof AxiosError
      ? error.response?.status
      : undefined
}

export const handleError = (
  error: unknown,
  fallbackMessage?: string,
  onRetry?: () => void,
) => {
  if (
    (isCUI && isOFLogoutError(error)) ||
    error instanceof CancelError ||
    isCancel(error)
  ) {
    return
  }

  if (error instanceof ZodError && error.errors[0]?.message) {
    return toast.error(error.errors[0].message)
  }

  // Disallow retry for client errors
  const allowRetry = !getErrorCode(error)?.toString().startsWith('4')

  toast.error(
    getErrorMessage(error, fallbackMessage),
    onRetry && allowRetry
      ? {
          actionText: 'Retry',
          action: onRetry,
        }
      : undefined,
  )
}
